import React from 'react';
import { useLocale } from 'locale';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import {
  CardCover,
  CardUpperSection,
  CardContentSection,
  CardContentDetails,
  TopTenBadgePosition,
  ContactButton,
  Root,
  AgencyName,
  AgencyNameH2,
  CardCoverBG,
} from './styled';
import { TopTenBadge, TopTenBadgeVariant } from 'components/madad/top-ten-badge';
import { PropertiesInfo } from './PropertiesInfo';
import { OfficeAvatar } from './OfficeAvatar';
import { PhoneAnchor } from 'components/madad/phone-anchor/PhoneAnchor';
import { noopKeypress } from 'components/madad/utils';
import { variantToImagePath } from 'screens/MadadPage/card/consts';


export interface OfficeCardProps {
  id: string;
  isRegistered?: boolean;
  agencyName: string;
  avatarImage?: string;
  soldCount: number;
  exclusiveListingsCount: number;
  topTenBadgeVariant?: TopTenBadgeVariant;
  onContactClick: (id: string) => void;
  phone?: string;
  enablePhoneShowing?: boolean;
  size?: 'medium' | 'large';
  agencyNameHeadingLevel?: 2 | 3;
  isCarousel?: boolean;
}

export const OfficeCard: React.FC<OfficeCardProps> = React.memo(props => {
  const {
    id,
    isRegistered = true,
    agencyName,
    avatarImage,
    topTenBadgeVariant,
    onContactClick,
    phone,
    enablePhoneShowing,
    soldCount,
    exclusiveListingsCount,
    size = 'medium',
    agencyNameHeadingLevel = 3,
    isCarousel,
  } = props;
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const avatarImageUrl = avatarImage ? constructImageURI(avatarImage) : null;

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContactClick(id);
  };

  return (
    <Root isRegistered={isRegistered} size={size} isCarousel={isCarousel}>
      <CardUpperSection>
        <CardCover imgUrl={isRegistered ? avatarImageUrl : null} size={size} />
        {avatarImage ? <CardCoverBG size={size} /> : null}
        <OfficeAvatar imageUrl={isRegistered ? avatarImageUrl : null} size={size} alt={agencyName} />
        {topTenBadgeVariant ? (
          <TopTenBadgePosition>
            <TopTenBadge variant={topTenBadgeVariant} withShadow={isRegistered} variantToImagePath={variantToImagePath} />
          </TopTenBadgePosition>
        ) : null}
      </CardUpperSection>
      <CardContentSection>
        <CardContentDetails>
          {agencyNameHeadingLevel === 2 ? (
            <AgencyNameH2
              weight="bold"
              onKeyPress={noopKeypress}
              isRegistered={isRegistered}
            >
              {agencyName}
            </AgencyNameH2>
          ) : (
            <AgencyName
              weight="bold"
              onKeyPress={noopKeypress}
              isRegistered={isRegistered}
            >
              {agencyName}
            </AgencyName>
          )}
          <PropertiesInfo
            soldCount={soldCount}
            exclusiveListingsCount={exclusiveListingsCount}
            size={size}
          />
        </CardContentDetails>
        {isRegistered ? (
          enablePhoneShowing ? (
            <PhoneAnchor
              dataAuto="contact-agency-button"
              className="contact-agency-button"
              phone={phone}
              label={t('madad.contactAgency')}
              officeId={id}
              fullWidth={false}
            />
          ) : (
            <ContactButton data-auto="contact-agency-button" onClick={handleContactClick}>
              {t('madad.contactAgency')}
            </ContactButton>
          )
        ) : null}
      </CardContentSection>
    </Root>
  );
});
